import React, { useState } from "react";
import { legacyStorageUrl } from 'utils/fileUrl';
import PropTypes from "prop-types";
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Checkbox,
  Box,
  CircularProgress,
  IconButton,
  Collapse,
  Dialog,
  DialogContent,
  Zoom,
} from "@mui/material";
import { Info as InfoIcon, Close as CloseIcon, ZoomIn, ZoomOut } from "@mui/icons-material";
import { isFunction } from "utils/functions";

function CatalogView({
  columns,
  data,
  isLoading,
  selected,
  onRowsSelect,
  setCheck,
  verifyExist,
  visibleColumns,
}) {
  // Estado para el modal de imagen (debe estar antes de cualquier return)
  const [imageModal, setImageModal] = useState({ open: false, imageUrl: null, title: "" });
  const [zoom, setZoom] = useState(1);

  // Obtener columnas visibles
  const visibleCols = columns.filter(
    (col) => !col.name || visibleColumns.includes(col.name)
  );

  // Encontrar la primera columna con imagen/avatar (si existe)
  const imageColumn = visibleCols.find(
    (col) =>
      col.name &&
      (col.name.toLowerCase().includes("image") ||
        col.name.toLowerCase().includes("avatar") ||
        col.name.toLowerCase().includes("photo") ||
        col.name.toLowerCase().includes("picture") ||
        col.name.toLowerCase().includes("logo"))
  );

  // Encontrar columnas principales (título, nombre, código, etc.)
  const titleColumn =
    visibleCols.find(
      (col) =>
        col.name &&
        (col.name.toLowerCase().includes("name") ||
          col.name.toLowerCase().includes("title") ||
          col.name.toLowerCase().includes("nombre") ||
          col.name.toLowerCase().includes("titulo"))
    ) ||
    visibleCols.find(
      (col) =>
        col.name &&
        (col.name.toLowerCase().includes("code") ||
          col.name.toLowerCase().includes("codigo"))
    ) ||
    visibleCols.find((col) => col.name && col.name !== imageColumn?.name);

  // Encontrar columna de acciones
  const actionsColumn = columns.find(
    (col) => col.name && col.name.toLowerCase().includes("acciones")
  );

  // Obtener otras columnas para mostrar en el contenido
  const otherColumns = visibleCols.filter(
    (col) =>
      col.name &&
      !col.hideInCatalog &&
      col.name !== imageColumn?.name &&
      col.name !== titleColumn?.name &&
      col.name !== "acciones" &&
      col.name.toLowerCase() !== "logo"
  );

  if (isLoading || !Array.isArray(data) || data.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
          p: 3,
        }}
      >
        {isLoading && <CircularProgress color="secondary" />}
        {!isLoading && (
          <Typography variant="body1" color="text.secondary">
            No hay datos para mostrar...
          </Typography>
        )}
      </Box>
    );
  }

  const renderCellContent = (column, row) => {
    if (isFunction(column.component)) {
      return column.component(row);
    }
    return row[column.name] ?? "";
  };

  const getImageUrl = (row) => {
    const apiBase = process.env.REACT_APP_API_SERVER || '';
    const apiVersion = process.env.REACT_APP_API_VERSION || '';
    const clientLogoApiUrl = row?.id && row?.logo_path
      ? `${apiBase}${apiVersion}clients/${row.id}/logo`
      : null;
    // Buscar imagen en diferentes campos posibles (incluyendo imageBase64)
    const imageFields = [
      'imageBase64',
      'resourceImageBase64',
      'imageUrl',
      'image_url',
      'imagePath',
      'image_path',
      'logo_url',
      'logo_path',
      'image',
      'resourceImageUrl',
      'resource_image_url',
      'resourceImage'
    ];

    for (const field of imageFields) {
      const imageValue = row[field];
      if (imageValue) {
        // Si es base64 (data:image)
        if (typeof imageValue === "string" && imageValue.startsWith("data:image")) {
          return imageValue;
        }
        // Si es una URL completa
        if (typeof imageValue === "string" && (imageValue.startsWith("http") || imageValue.startsWith("/"))) {
          return imageValue;
        }
        // Si es un path relativo con carpetas (ej: admin/products/xx.png)
        if (typeof imageValue === "string" && imageValue.includes("/") && !imageValue.startsWith("http") && !imageValue.startsWith("/")) {
          // Para clientes, preferir endpoint API de logo (evita depender de /storage público)
          if (field === "logo_path" && clientLogoApiUrl) {
            return clientLogoApiUrl;
          }
          return legacyStorageUrl(imageValue);
        }
        // Si es solo el nombre del archivo, construir la URL
        if (typeof imageValue === "string" && !imageValue.includes("/") && !imageValue.startsWith("data:")) {
          return legacyStorageUrl(`resources/images/${imageValue}`);
        }
      }
    }

    // Si hay una columna de imagen específica con componente
    if (imageColumn && isFunction(imageColumn.component)) {
      const imageComponent = imageColumn.component(row);
      // Si el componente retorna un Avatar, extraer el src
      if (imageComponent && imageComponent.props && imageComponent.props.src) {
        return imageComponent.props.src;
      }
      // Si el componente retorna directamente una URL
      if (typeof imageComponent === "string") {
        return imageComponent;
      }
    }

    // Si hay una columna de imagen específica con valor directo
    if (imageColumn && row[imageColumn.name]) {
      const imageValue = row[imageColumn.name];
      if (typeof imageValue === "string") {
        if (imageValue.startsWith("data:image") || imageValue.startsWith("http") || imageValue.startsWith("/")) {
          return imageValue;
        }
        if (imageValue.includes("/") && !imageValue.startsWith("http") && !imageValue.startsWith("/")) {
          return legacyStorageUrl(imageValue);
        }
        if (!imageValue.includes("/")) {
          return legacyStorageUrl(`resources/images/${imageValue}`);
        }
      }
    }

    return null;
  };

  // Componente interno para cada tarjeta
  const CatalogCard = ({
    row,
    index,
    isSelected,
    imageUrl,
    titleValue,
    titleText,
    visibleFields,
    hiddenFields,
    hasMoreFields,
    onSelect,
    imageColumn,
    actionsColumn,
    renderCellContent,
    onRowsSelect,
    onImageClick
  }) => {
    const [expanded, setExpanded] = useState(false);

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={`card-${index}`}>
        <Card
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            position: "relative",
            cursor: onRowsSelect ? "pointer" : "default",
            border: isSelected ? 2 : 1,
            borderColor: isSelected ? "primary.main" : "divider",
            borderRadius: 2,
            overflow: "hidden",
            "&:hover": {
              boxShadow: 2,
              transform: "translateY(-1px)",
              transition: "all 0.2s ease",
            },
          }}
          onClick={() => {
            if (onRowsSelect) {
              onSelect(row);
            }
          }}
        >
          {onRowsSelect && (
            <Checkbox
              checked={isSelected}
              onChange={() => onSelect(row)}
              onClick={(e) => e.stopPropagation()}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 1,
              }}
              color="primary"
            />
          )}

          <Box
            sx={{
              width: 88,
              minWidth: 88,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: row?.color || "grey.50",
              borderRight: 1,
              borderColor: "divider",
              p: 1,
            }}
            onClick={(e) => {
              if (imageUrl) {
                e.stopPropagation();
                onImageClick(imageUrl, titleValue);
              }
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={titleValue}
                sx={{
                  width: 60,
                  height: 60,
                  objectFit: "cover",
                  borderRadius: 2,
                  bgcolor: "common.white",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: "common.white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  color: "text.secondary",
                }}
              >
                {titleText
                  ? titleText.charAt(0).toUpperCase()
                  : "?"}
              </Box>
            )}
          </Box>

          <CardContent sx={{ flexGrow: 1, p: 1.5, pr: 1, position: "relative", pb: actionsColumn ? 6 : 1.5, minHeight: 120 }}>
            {titleColumn && (
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  fontSize: "0.95rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {titleValue}
              </Typography>
            )}

            {/* Mostrar solo los primeros 3 campos en una sola línea */}
            {visibleFields.map((column) => {
              const content = renderCellContent(column, row);
              if (!content && content !== 0) return null;

              return (
              <Typography
                key={column.name}
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  mb: 0.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                  <Box component="span" sx={{ fontWeight: 600, color: "text.secondary" }}>
                    {column.label || column.name}:
                  </Box>{" "}
                  <Box component="span">{content}</Box>
                </Typography>
              );
            })}

            {/* Botón INFO para mostrar campos adicionales */}
            {hasMoreFields && (
              <Box
                sx={{
                  mt: 0.5,
                  display: "flex",
                  justifyContent: "flex-start",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.light",
                      color: "primary.contrastText",
                    },
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* Campos adicionales colapsables */}
            <Collapse in={expanded}>
              <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}>
                {hiddenFields.map((column) => {
                  const content = renderCellContent(column, row);
                  if (!content && content !== 0) return null;

                  return (
                    <Typography
                      key={column.name}
                      variant="body2"
                      sx={{
                        fontSize: "0.8rem",
                        mb: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 600, color: "text.secondary" }}>
                        {column.label || column.name}:
                      </Box>{" "}
                      <Box component="span">{content}</Box>
                    </Typography>
                  );
                })}
              </Box>
            </Collapse>

            {/* Mostrar botones de acción si existen */}
            {actionsColumn && null}
          </CardContent>

          {actionsColumn && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 88,
                right: 0,
                height: 44,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                pr: 1.5,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {renderCellContent(actionsColumn, row)}
            </Box>
          )}
        </Card>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {data.map((row, index) => {
          const isSelected = verifyExist(row) >= 0;
          const imageUrl = getImageUrl(row);
          const titleValue = titleColumn
            ? renderCellContent(titleColumn, row)
            : `Item ${index + 1}`;
          const titleText = typeof titleValue === 'string'
            ? titleValue
            : row?.title || row?.name || row?.file_name || row?.code || '';

          // Separar columnas visibles y ocultas
          const visibleFields = otherColumns.slice(0, 3);
          const hiddenFields = otherColumns.slice(3);
          const hasMoreFields = hiddenFields.length > 0;

          return (
            <CatalogCard
              key={`card-${index}`}
              row={row}
              index={index}
              isSelected={isSelected}
              imageUrl={imageUrl}
              titleValue={titleValue}
              titleText={titleText}
              visibleFields={visibleFields}
              hiddenFields={hiddenFields}
              hasMoreFields={hasMoreFields}
              onSelect={setCheck}
              imageColumn={imageColumn}
              actionsColumn={actionsColumn}
              renderCellContent={renderCellContent}
              onRowsSelect={onRowsSelect}
              onImageClick={(url, title) => {
                setImageModal({ open: true, imageUrl: url, title });
                setZoom(1);
              }}
            />
          );
        })}
      </Grid>

      {/* Modal de imagen ampliada */}
      <Dialog
        open={imageModal.open}
        onClose={() => setImageModal({ open: false, imageUrl: null, title: "" })}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.9)",
            boxShadow: "none",
          },
        }}
      >
        <DialogContent
          sx={{
            position: "relative",
            p: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "70vh",
            overflow: "hidden",
          }}
        >
          {/* Botón cerrar */}
          <IconButton
            onClick={() => setImageModal({ open: false, imageUrl: null, title: "" })}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "white",
              bgcolor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1,
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.7)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Controles de zoom */}
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              zIndex: 1,
            }}
          >
            <IconButton
              onClick={() => setZoom((prev) => Math.min(prev + 0.25, 3))}
              disabled={zoom >= 3}
              sx={{
                color: "white",
                bgcolor: "rgba(0, 0, 0, 0.5)",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                },
                "&.Mui-disabled": {
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              <ZoomIn />
            </IconButton>
            <IconButton
              onClick={() => setZoom((prev) => Math.max(prev - 0.25, 0.5))}
              disabled={zoom <= 0.5}
              sx={{
                color: "white",
                bgcolor: "rgba(0, 0, 0, 0.5)",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                },
                "&.Mui-disabled": {
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              <ZoomOut />
            </IconButton>
          </Box>

          {/* Título */}
          {imageModal.title && (
            <Typography
              variant="h6"
              sx={{
                position: "absolute",
                top: 8,
                left: "50%",
                transform: "translateX(-50%)",
                color: "white",
                bgcolor: "rgba(0, 0, 0, 0.5)",
                px: 2,
                py: 1,
                borderRadius: 1,
                zIndex: 1,
              }}
            >
              {imageModal.title}
            </Typography>
          )}

          {/* Imagen ampliada */}
          {imageModal.imageUrl && (
            <Box
              component="img"
              src={imageModal.imageUrl}
              alt={imageModal.title}
              sx={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                transform: `scale(${zoom})`,
                transition: "transform 0.3s ease",
                cursor: zoom > 1 ? "move" : "default",
              }}
              draggable={false}
            />
          )}

          {/* Indicador de zoom */}
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              color: "white",
              bgcolor: "rgba(0, 0, 0, 0.5)",
              px: 2,
              py: 0.5,
              borderRadius: 1,
              zIndex: 1,
            }}
          >
            {Math.round(zoom * 100)}%
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

CatalogView.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  selected: PropTypes.arrayOf(PropTypes.object),
  onRowsSelect: PropTypes.func,
  setCheck: PropTypes.func,
  verifyExist: PropTypes.func,
  visibleColumns: PropTypes.arrayOf(PropTypes.string),
};

export default CatalogView;
